import { Link } from 'react-router-dom';
import type { ComponentType, SVGProps } from 'react';
import {
  Phone, MapPin, Mail, MessageSquare, FileText, Calendar, CheckSquare, Check, ChevronRight, Loader2, CheckCircle2, UserX,
} from 'lucide-react';
import type { TodayAgendaItem, TodayAgendaPerson } from '../../types';

type IconC = ComponentType<SVGProps<SVGSVGElement>>;

const personName = (p: TodayAgendaPerson | null): string => {
  if (!p) return '';
  const f = p.crmFirstName ?? p.firstName ?? '';
  const l = p.crmLastName ?? p.lastName ?? '';
  return `${f} ${l}`.trim();
};

const iconFor = (item: TodayAgendaItem): IconC => {
  if (item.isCoachMoveReport) return UserX;
  if (item.kind === 'task') return CheckSquare;
  switch (item.activityType) {
    case 'CALL': return Phone;
    case 'IN_PERSON': return MapPin;
    case 'EMAIL': return Mail;
    case 'TEXT': return MessageSquare;
    case 'NOTE': return FileText;
    default: return Calendar;
  }
};

const timeLabel = (at: string | null): string => {
  if (!at) return '';
  return new Date(at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const overdueLabel = (at: string | null): string => {
  if (!at) return 'Overdue';
  const days = Math.floor((Date.now() - new Date(at).getTime()) / (24 * 60 * 60 * 1000));
  if (days <= 0) return 'Earlier today';
  if (days === 1) return '1 day overdue';
  return `${days} days overdue`;
};

const targetFor = (item: TodayAgendaItem): string | null =>
  item.person
    ? item.isCoachMoveReport
      ? `/people/${item.person.id}?coachFlag=1` // straight into the prefilled review
      : `/people/${item.person.id}`
    : item.org
      ? `/organizations/${item.org.id}`
      : null;

function Row({
  item,
  onComplete,
  completing,
}: {
  item: TodayAgendaItem;
  onComplete: (id: string) => void;
  completing: boolean;
}) {
  const Icon = iconFor(item);
  const target = targetFor(item);
  const subtitleBits = [
    item.org?.name,
    item.bucket === 'overdue' ? overdueLabel(item.at) : timeLabel(item.at),
    item.meetingLocation === 'ON_CAMPUS' ? 'on-campus' : null,
  ].filter(Boolean) as string[];

  const content = (
    <>
      <div
        className={`flex-shrink-0 h-9 w-9 rounded-full flex items-center justify-center ${
          item.isCoachMoveReport
            ? 'bg-amber-100 dark:bg-amber-900/30'
            : item.bucket === 'overdue'
              ? 'bg-rose-100 dark:bg-rose-900/30'
              : 'bg-blast-100 dark:bg-blast-900/30'
        }`}
      >
        <Icon
          className={`h-4 w-4 ${
            item.isCoachMoveReport
              ? 'text-amber-600 dark:text-amber-400'
              : item.bucket === 'overdue'
                ? 'text-rose-600 dark:text-rose-400'
                : 'text-blast-700 dark:text-blast-300'
          }`}
        />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
          {item.title || (item.kind === 'task' ? 'Task' : 'Activity')}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
          {personName(item.person) && `${personName(item.person)} · `}
          {subtitleBits.join(' · ') || (item.kind === 'task' ? 'Task' : '')}
        </p>
      </div>
    </>
  );

  return (
    <div className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
      {target ? (
        <Link to={target} className="flex items-center gap-3 min-w-0 flex-1">{content}</Link>
      ) : (
        <div className="flex items-center gap-3 min-w-0 flex-1">{content}</div>
      )}

      {item.kind === 'task' ? (
        <button
          type="button"
          onClick={() => onComplete(item.id)}
          disabled={completing}
          aria-label="Mark task complete"
          className="flex-shrink-0 inline-flex items-center gap-1 rounded-full border border-gray-200 dark:border-gray-700 px-2.5 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 hover:border-blast-400 hover:text-blast-700 dark:hover:text-blast-300 disabled:opacity-50 transition-colors"
        >
          {completing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          <span className="hidden sm:inline">Done</span>
        </button>
      ) : target ? (
        <Link to={target} aria-label="Open" className="flex-shrink-0 text-gray-300 dark:text-gray-600 hover:text-blast-500">
          <ChevronRight className="h-5 w-5" />
        </Link>
      ) : null}
    </div>
  );
}

/**
 * The merged overdue→today agenda (tasks + planned activities). Tasks complete
 * inline; activities tap through to the person to log in context.
 */
export function AgendaList({
  items,
  onComplete,
  completingIds,
  followUpDue,
}: {
  items: TodayAgendaItem[];
  onComplete: (id: string) => void;
  completingIds: Set<string>;
  followUpDue: number;
}) {
  const overdue = items.filter((i) => i.bucket === 'overdue');
  const today = items.filter((i) => i.bucket === 'today');

  if (items.length === 0) {
    return (
      <div className="card p-8 text-center">
        <CheckCircle2 className="h-10 w-10 mx-auto mb-3 text-blast-500 dark:text-blast-400" />
        <p className="text-base font-semibold text-gray-900 dark:text-gray-100">All clear for today 🎉</p>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {followUpDue > 0
            ? `${followUpDue} follow-up${followUpDue === 1 ? '' : 's'} are waiting when you're ready.`
            : 'No visits, calls, or tasks on the books.'}
        </p>
        {followUpDue > 0 && (
          <Link to="/people?scope=mine&segment=followUpDue" className="btn-secondary text-sm mt-4 inline-flex">
            See follow-ups
          </Link>
        )}
      </div>
    );
  }

  return (
    <div className="card p-4 sm:p-5">
      {overdue.length > 0 && (
        <>
          <h4 className="text-[11px] font-semibold uppercase tracking-wider text-rose-500 dark:text-rose-400 mb-1.5 px-2.5">
            Overdue
          </h4>
          <div className="space-y-0.5 mb-3">
            {overdue.map((i) => (
              <Row key={`${i.kind}-${i.id}`} item={i} onComplete={onComplete} completing={completingIds.has(i.id)} />
            ))}
          </div>
        </>
      )}
      {today.length > 0 && (
        <>
          <h4 className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1.5 px-2.5">
            Today
          </h4>
          <div className="space-y-0.5">
            {today.map((i) => (
              <Row key={`${i.kind}-${i.id}`} item={i} onComplete={onComplete} completing={completingIds.has(i.id)} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
